import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { playersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Trash2, Edit, User, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor, getStatusBgColor } from '../lib/utils';

const positions = [
  'Quarterback', 'Running Back', 'Wide Receiver', 'Tight End',
  'Offensive Lineman', 'Defensive Lineman', 'Linebacker',
  'Cornerback', 'Safety', 'Kicker', 'Punter'
];

const SPORTS = [
  'Football', 'Basketball', 'Baseball', 'Volleyball', 'Cheer', 
  'Track', 'Cross Country', 'Soccer', 'Softball', 'Wrestling'
];

export default function RosterPage() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    jersey_number: '',
    sport: 'Football',
    height: '',
    target_weight: '',
    phone: '',
    parent_name: '',
    parent_phone: '',
    locker_number: '',
    shirt_size: '',
    short_size: '',
    shoe_size: '',
    group_number: ''
  });

  useEffect(() => {
    loadPlayers();
  }, []);

  useEffect(() => {
    filterPlayers();
  }, [players, searchQuery, statusFilter, sportFilter]);

  const loadPlayers = async () => {
    try {
      const res = await playersAPI.getAll();
      setPlayers(res.data);
    } catch (error) {
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const filterPlayers = () => {
    let filtered = [...players];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) || 
        p.position.toLowerCase().includes(query) ||
        p.jersey_number.toString().includes(query)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    if (sportFilter !== 'all') {
      filtered = filtered.filter(p => p.sport === sportFilter);
    }
    
    setFilteredPlayers(filtered);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      jersey_number: '',
      sport: 'Football',
      height: '',
      target_weight: '',
      phone: '',
      parent_name: '',
      parent_phone: '',
      locker_number: '',
      shirt_size: '',
      short_size: '',
      shoe_size: '',
      group_number: ''
    });
    setEditingPlayer(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      jersey_number: parseInt(formData.jersey_number),
      target_weight: formData.target_weight ? parseFloat(formData.target_weight) : null
    };

    try {
      if (editingPlayer) {
        await playersAPI.update(editingPlayer.id, data);
        toast.success('Player updated');
      } else {
        await playersAPI.create(data);
        toast.success('Player added');
      }
      setDialogOpen(false);
      resetForm();
      loadPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save player');
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name,
      position: player.position,
      jersey_number: player.jersey_number.toString(),
      sport: player.sport || 'Football',
      height: player.height || '',
      target_weight: player.target_weight?.toString() || '',
      phone: player.phone || '',
      parent_name: player.parent_name || '',
      parent_phone: player.parent_phone || '',
      locker_number: player.locker_number || '',
      shirt_size: player.shirt_size || '',
      short_size: player.short_size || '',
      shoe_size: player.shoe_size || '',
      group_number: player.group_number || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this player?')) return;
    
    try {
      await playersAPI.delete(id);
      toast.success('Player deleted');
      loadPlayers();
    } catch (error) {
      toast.error('Failed to delete player');
    }
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await playersAPI.uploadCSV(file);
      if (result.data.added > 0) {
        toast.success(result.data.message);
        loadPlayers();
      }
      if (result.data.errors?.length > 0) {
        result.data.errors.forEach(err => toast.error(err));
      }
      setCsvDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    const headers = ['name', 'position', 'jersey_number', 'group_number', 'height', 'target_weight', 'phone', 'parent_name', 'parent_phone', 'locker_number', 'shirt_size', 'short_size', 'shoe_size'];
    const exampleRow = ['John Smith', 'Quarterback', '12', '1', '6\'2"', '220', '555-0123', 'Mike Smith', '555-0124', '42', 'L', 'L', '11'];
    
    const csvContent = [headers.join(','), exampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roster_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="overline">Team Management</p>
            <h1 className="font-display text-4xl lg:text-5xl text-white mt-2">ROSTER</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* CSV Upload Dialog */}
            <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
                  data-testid="upload-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">IMPORT ROSTER</DialogTitle>
                  <DialogDescription className="text-[#71717A]">
                    Upload a CSV file to bulk import players
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-sm text-[#A1A1AA] mb-2">Required columns:</p>
                    <p className="text-xs text-[#71717A] font-mono">name, position, jersey_number</p>
                    <p className="text-sm text-[#A1A1AA] mt-3 mb-2">Optional columns:</p>
                    <p className="text-xs text-[#71717A] font-mono">group_number, height, target_weight, phone, parent_name, parent_phone, locker_number, shirt_size, short_size, shoe_size</p>
                  </div>
                  
                  <Button
                    onClick={downloadTemplate}
                    variant="outline"
                    className="w-full border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1A1A]"
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  
                  <div className="border-2 border-dashed border-[#27272A] p-8 text-center hover:border-[#FF3B30] transition-colors">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                      id="csv-upload"
                      data-testid="csv-file-input"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-[#71717A]" />
                      <p className="text-white font-medium">
                        {uploading ? 'Uploading...' : 'Click to upload CSV'}
                      </p>
                      <p className="text-xs text-[#71717A] mt-1">or drag and drop</p>
                    </label>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Player Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white btn-press"
                  data-testid="add-player-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Player
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editingPlayer ? 'EDIT PLAYER' : 'ADD PLAYER'}
                </DialogTitle>
                <DialogDescription className="text-[#71717A]">
                  {editingPlayer ? 'Update player information' : 'Add a new player to the roster'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-[#A1A1AA]">Full Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="John Smith"
                      data-testid="player-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Jersey #</Label>
                    <Input
                      type="number"
                      value={formData.jersey_number}
                      onChange={(e) => setFormData({...formData, jersey_number: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="12"
                      data-testid="player-jersey-input"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Sport</Label>
                    <Select
                      value={formData.sport}
                      onValueChange={(value) => setFormData({...formData, sport: value})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="player-sport-select">
                        <SelectValue placeholder="Select Sport" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {SPORTS.map(sport => (
                          <SelectItem key={sport} value={sport} className="text-white hover:bg-[#27272A]">
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Position</Label>
                    <Select
                      value={formData.position}
                      onValueChange={(value) => setFormData({...formData, position: value})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="player-position-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {positions.map(pos => (
                          <SelectItem key={pos} value={pos} className="text-white hover:bg-[#27272A]">
                            {pos}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Height</Label>
                    <Input
                      value={formData.height}
                      onChange={(e) => setFormData({...formData, height: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="6'2&quot;"
                      data-testid="player-height-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Group #</Label>
                    <Input
                      value={formData.group_number}
                      onChange={(e) => setFormData({...formData, group_number: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="1"
                      data-testid="player-group-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Target Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={formData.target_weight}
                      onChange={(e) => setFormData({...formData, target_weight: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="225"
                      data-testid="player-target-weight-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Player Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="555-0123"
                      data-testid="player-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Parent Name</Label>
                    <Input
                      value={formData.parent_name}
                      onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="Parent name"
                      data-testid="player-parent-name-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[#A1A1AA]">Parent Phone</Label>
                    <Input
                      value={formData.parent_phone}
                      onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="555-0124"
                      data-testid="player-parent-phone-input"
                    />
                  </div>
                  
                  {/* Equipment Sizes */}
                  <div className="col-span-2 pt-4 border-t border-[#27272A]">
                    <p className="text-xs text-[#71717A] uppercase tracking-wider mb-3">Equipment Info</p>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Locker #</Label>
                    <Input
                      value={formData.locker_number}
                      onChange={(e) => setFormData({...formData, locker_number: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="42"
                      data-testid="player-locker-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Shirt Size</Label>
                    <Select
                      value={formData.shirt_size}
                      onValueChange={(value) => setFormData({...formData, shirt_size: value})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="player-shirt-size">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'].map(size => (
                          <SelectItem key={size} value={size} className="text-white hover:bg-[#27272A]">
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Short Size</Label>
                    <Select
                      value={formData.short_size}
                      onValueChange={(value) => setFormData({...formData, short_size: value})}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white" data-testid="player-short-size">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'].map(size => (
                          <SelectItem key={size} value={size} className="text-white hover:bg-[#27272A]">
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Shoe Size</Label>
                    <Input
                      value={formData.shoe_size}
                      onChange={(e) => setFormData({...formData, shoe_size: e.target.value})}
                      className="mt-1 bg-[#0A0A0A] border-[#27272A] text-white"
                      placeholder="10.5"
                      data-testid="player-shoe-size-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white"
                  data-testid="save-player-btn"
                >
                  {editingPlayer ? 'Update Player' : 'Add Player'}
                </Button>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search players..."
              className="pl-10 bg-[#121212] border-[#27272A] text-white"
              data-testid="search-players-input"
            />
          </div>
          <Select value={sportFilter} onValueChange={setSportFilter}>
            <SelectTrigger className="w-full sm:w-44 bg-[#121212] border-[#27272A] text-white" data-testid="sport-filter-select">
              <SelectValue placeholder="Sport" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all" className="text-white">All Sports</SelectItem>
              {SPORTS.map(sport => (
                <SelectItem key={sport} value={sport} className="text-white">{sport}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 bg-[#121212] border-[#27272A] text-white" data-testid="status-filter-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="healthy" className="text-white">Healthy</SelectItem>
              <SelectItem value="injured" className="text-white">Injured</SelectItem>
              <SelectItem value="recovering" className="text-white">Recovering</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Players Table */}
        <Card className="bg-[#121212] border-[#27272A]" data-testid="roster-table">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-[#71717A]">Loading...</div>
              </div>
            ) : filteredPlayers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Player</th>
                      <th>Position</th>
                      <th>Status</th>
                      <th>Weight</th>
                      <th>Target</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlayers.map((player) => (
                      <tr key={player.id} data-testid={`player-row-${player.id}`}>
                        <td>
                          <span className="font-bold text-white">#{player.jersey_number}</span>
                        </td>
                        <td>
                          <button
                            onClick={() => navigate(`/player/${player.id}`)}
                            className="flex items-center gap-3 hover:text-[#FF3B30] transition-colors"
                          >
                            <div className="w-10 h-10 bg-[#27272A] flex items-center justify-center">
                              <User className="w-5 h-5 text-[#71717A]" />
                            </div>
                            <span className="text-white font-medium">{player.name}</span>
                          </button>
                        </td>
                        <td className="text-[#A1A1AA]">{player.position}</td>
                        <td>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium border ${getStatusBgColor(player.status)} ${getStatusColor(player.status)}`}>
                            {player.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="text-white font-medium">
                          {player.current_weight ? `${player.current_weight} lbs` : '-'}
                        </td>
                        <td className="text-[#A1A1AA]">
                          {player.target_weight ? `${player.target_weight} lbs` : '-'}
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(player)}
                              className="text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]"
                              data-testid={`edit-player-${player.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(player.id)}
                              className="text-[#A1A1AA] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              data-testid={`delete-player-${player.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                <User className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg mb-2">No players found</p>
                <p className="text-sm">Add your first player to get started</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {players.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-[#121212] border border-[#27272A] text-center">
              <p className="font-display text-2xl text-[#32D74B]">
                {players.filter(p => p.status === 'healthy').length}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Healthy</p>
            </div>
            <div className="p-4 bg-[#121212] border border-[#27272A] text-center">
              <p className="font-display text-2xl text-[#FFD60A]">
                {players.filter(p => p.status === 'recovering').length}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Recovering</p>
            </div>
            <div className="p-4 bg-[#121212] border border-[#27272A] text-center">
              <p className="font-display text-2xl text-[#FF3B30]">
                {players.filter(p => p.status === 'injured').length}
              </p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Injured</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
